import React, {
    useRef,
    useState,
    useEffect,
    useLayoutEffect,
    useCallback,
} from 'react';
import styled, { css } from 'styled-components';
import Slider from 'react-slick';
import { UIKitMark, ChevronIcon, PlayIcon, VolumeUpIcon } from '@kespa/ui';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import howWeTeach from '../images/how-we-teach.png';
import lingvoPortrait from '../images/lingvo-portret.png';
import articulation from '../images/articulation.png';
import speechFormulas from '../images/speech-formulas.png';
import formulas from '../images/formulas.png';

import LinesIcon from '../images/lines-icon.svg';
import WaveIcon from '../images/wave-icon.svg';

import speechDrilling from '../images/speech-drilling.mp3';

const H2 = styled.h2`
    font-family: 'Futura PT', sans-serif;
    font-weight: 700;
    font-size: 70px;
    line-height: 100%;
    color: #000;
    margin-bottom: 40px;

    @media screen and (max-width: 1024px) {
        font-size: 48px;
        margin-bottom: 32px;
    }

    @media screen and (max-width: 920px) {
        font-size: 42px;
    }

    @media screen and (max-width: 760px) {
        font-size: 28px;
    }
`;

const StyledParagraph = styled.p`
    font-family: 'Circe Web', sans-serif;
    font-weight: normal;
    font-size: 22px;
    margin-bottom: 24px;
    line-height: 140%;
    color: #404040;
    img {
        max-width: 100%;
    }

    @media screen and (max-width: 1024px) {
        font-size: 18px;
        margin-bottom: 16px;
    }

    @media screen and (max-width: 760px) {
        font-size: 14px;
        margin-bottom: 8px;
        span[color] {
            padding: 0 3px;
        }
    }
`;

const TeachingBlock = styled.div`
    padding: 105px 0 0;
    box-sizing: border-box;
    margin-bottom: 184px;

    @media screen and (max-width: 1024px) {
        padding: 80px 0 0;
        margin-bottom: 148px;
    }
    @media screen and (max-width: 760px) {
        padding: 64px 0 0;
        background: none;
        display: block;
        margin-bottom: 60px;
    }
`;

const StyledSliderElement = styled.div`
    outline: none;
`;

const StyledSliderElementInner = styled.div`
    display: flex;
    align-items: center;
    min-height: 600px;
    overflow: hidden;

    > div {
        max-width: 50%;
    }

    @media screen and (max-width: 1024px) {
        min-height: 500px;
    }
    @media screen and (max-width: 760px) {
        flex-direction: column-reverse;
        min-height: initial;
        > div {
            max-width: 100%;
            margin-top: 32px;
        }
    }
`;

const StyledIllustration = styled.img`
    width: 45%;
    margin-left: 32px;
    height: auto;

    @media screen and (max-width: 760px) {
        width: auto;
        margin-left: auto;
        margin: 0 auto;
        height: 300px;
    }
`;

const StyledStories = styled.div`
    width: 100%;
    display: flex;
    margin-bottom: 64px;
    position: relative;
`;

const StyledStory = styled.div`
    flex-grow: 1;
    margin-right: 32px;
    color: ${({ active }) => (active ? '#0B9CDB' : '#666666')};
    cursor: pointer;
    font-family: 'Circe Web', sans-serif;

    @media screen and (max-width: 760px) {
        margin-right: 8px;
        p {
            display: none;
        }
        p.active {
            display: block;
            position: absolute;
            left: 0;
        }
    }

    &:last-child {
        margin-right: 0;
    }

    &:hover {
        opacity: 0.8;
    }
`;

const StyledProgress = styled.div`
    width: 100%;
    height: 8px;
    border-radius: 8px;
    background: #f2f2f2;
    margin-bottom: 16px;
    position: relative;
    overflow: hidden;
`;

const StyledProgressBar = styled.div`
    background: #0dbdfc;
    height: 8px;
    border-radius: 8px;
    width: 0;
    position: absolute;
    top: 0;
    left: 0;
    ${({ filled }) =>
        filled &&
        css`
            min-width: 100%;
            max-width: 100%;
        `}
    ${({ animated }) =>
        animated &&
        css`
            transition: width 1s linear;
        `}
`;

const StyledNavButton = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 10px 15px 50px -5px rgba(0, 0, 0, 0.15);
    position: absolute;
    z-index: 9;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transform: translateY(-50%);
    transition: all 0.2s ease-in-out;
    &:hover {
        height: 66px;
        width: 66px;
    }
    svg {
        min-width: 32px;
        min-height: 32px;
    }

    ${({ left }) =>
        left
            ? css`
                  right: calc(45% - 32px);
                  top: 300px;
              `
            : css`
                  right: calc(5% - 32px);
                  top: 300px;
              `}

    @media screen and (max-width: 1024px) {
        width: 48px;
        height: 48px;
        &:hover {
            height: 50px;
            width: 50px;
        }
    }
    @media screen and (max-width: 760px) {
        ${({ left }) =>
            left
                ? css`
                      left: -2%;
                      top: 150px;
                  `
                : css`
                      right: -2%;
                      top: 150px;
                  `}
    }
`;

const StyledIcon = styled.img`
    margin-right: 16px;
    margin-top: 8px;
`;

const StyledSpeechDrilling = styled.div`
    font-family: AdobeTextPro, serif;
    font-size: 20px;
    line-height: 140%;
    background: #fff;
    display: inline-block;
    padding: 8px;
    margin-bottom: 16px;
    box-shadow: 10px 15px 50px -5px rgba(0, 0, 0, 0.15);
    align-items: center;
    display: inline-flex;
    border-radius: 8px;

    @media screen and (max-width: 760px) {
        font-size: 14px;
    }
`;

const StyledSpeechDrillingIcon = styled.div`
    width: 24px;
    height: 24px;
    max-width: 24px;
    max-height: 24px;
    border-radius: 50%;
    border: 2px solid #0b9cdb;
    fill: #0b9cdb;
    display: inline-flex;
    margin-right: 8px;
    cursor: pointer;

    svg {
        position: relative;
        width: 100%;
        height: 100%;
    }

    @media screen and (max-width: 760px) {
        width: 20px;
        height: 20px;
    }
`;

const SLIDES_INTERVAL = 10;

const NextArrow = props => {
    const { onClick } = props;
    return (
        <StyledNavButton onClick={onClick}>
            <ChevronIcon direction="right" />
        </StyledNavButton>
    );
};

const PrevArrow = props => {
    const { onClick } = props;
    return (
        <StyledNavButton onClick={onClick} left={true}>
            <ChevronIcon direction="left" />
        </StyledNavButton>
    );
};

export const TeachingSlider = ({ isVisible }) => {
    const sliderEl = useRef(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [playing, setPlaying] = useState(true);
    const [timing, setTiming] = useState(0);
    const [audioPlaying, setAudioPlaying] = useState(false);
    let interval = null;

    const storiesBars = [
        'Как мы преподаем?',
        'Лингвопортрет',
        'Речевые формулы',
        'Артикуляционная память',
    ];

    const audioRef = useRef(null);

    useLayoutEffect(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio(speechDrilling);
            audioRef.current.addEventListener('ended', e => {
                setAudioPlaying(false);
            });
        } else if (audioPlaying) {
            audioRef.current.play();
            audioRef.current.currentTime = 0;
        } else {
            audioRef.current.pause();
        }
    }, [audioPlaying]);

    const toggleAudio = useCallback(() => {
        setAudioPlaying(p => !p);
    }, []);

    useEffect(() => {
        if (currentSlide <= storiesBars.length - 1 && playing && isVisible) {
            interval = setInterval(() => {
                setTiming(timing => timing + 1);
            }, 1000);
        }
        return () => {
            clearInterval(interval);
        };
    }, [currentSlide, playing, isVisible]);

    useLayoutEffect(() => {
        if (timing > SLIDES_INTERVAL) {
            if (currentSlide === storiesBars.length - 1) {
                setPlaying(false);
                setTimeout(() => {
                    setTiming(SLIDES_INTERVAL);
                }, 0);
            } else {
                sliderEl.current.slickGoTo(currentSlide + 1);
                setTimeout(() => {
                    setTiming(0);
                }, 0);
            }
        }
    }, [timing]);

    const sliderSettings = {
        dots: false,
        infinite: true,
        fade: true,
        adaptiveHeight: true,
        speed: 400,
        centerPadding: '60px',
        slidesToShow: 1,
        slidesToScroll: 1,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        beforeChange: (oldIndex, index) => {
            if (timing < SLIDES_INTERVAL) {
                setPlaying(false);
                setTiming(SLIDES_INTERVAL);
            }
            setCurrentSlide(index);
        },
        onSwipe: () => {
            setPlaying(false);
            setTiming(SLIDES_INTERVAL);
        },
    };

    return (
        <React.Fragment>
            <TeachingBlock>
                <StyledStories id="our-method">
                    {storiesBars.map((story, index) => {
                        let progress = 0;
                        if (currentSlide === index) {
                            progress = (100 * timing) / SLIDES_INTERVAL;
                        }
                        if (currentSlide > index) {
                            progress = 100;
                        }
                        return (
                            <StyledStory
                                key={story}
                                onClick={() => {
                                    setPlaying(false);
                                    sliderEl.current.slickGoTo(index);
                                    setTiming(SLIDES_INTERVAL);
                                }}
                                active={index === currentSlide}
                            >
                                <StyledProgress>
                                    <StyledProgressBar
                                        style={{ width: `${progress}%` }}
                                        filled={
                                            index < currentSlide && !playing
                                        }
                                        animated={
                                            index === currentSlide && playing
                                        }
                                    />
                                </StyledProgress>
                                <p
                                    className={`${
                                        index === currentSlide ? 'active' : ''
                                    }`}
                                >
                                    {story}
                                </p>
                            </StyledStory>
                        );
                    })}
                </StyledStories>

                <Slider ref={sliderEl} {...sliderSettings}>
                    <StyledSliderElement>
                        <StyledSliderElementInner>
                            <div>
                                <H2>Как мы преподаем?</H2>
                                <StyledParagraph>
                                    КЭСПА&nbsp;&mdash; это аббревиатура вопроса
                                    &laquo;Как Это Сказать По-Английски?&raquo;
                                    и&nbsp;название нашей методики, которая
                                    существует более 20&nbsp;лет.
                                </StyledParagraph>

                                <StyledParagraph>
                                    Методика основывается на&nbsp;принципе:
                                </StyledParagraph>

                                <StyledParagraph>
                                    <UIKitMark color="yellow">
                                        Фраза на русском
                                    </UIKitMark>{' '}
                                    →{' '}
                                    <UIKitMark color="green">
                                        Фраза на английском
                                    </UIKitMark>
                                </StyledParagraph>
                                <StyledParagraph>
                                    Наша задача&nbsp;&mdash; доказать, что
                                    каждый может начать говорить по-английски.
                                </StyledParagraph>
                            </div>
                            <StyledIllustration src={howWeTeach} />
                        </StyledSliderElementInner>
                    </StyledSliderElement>

                    <StyledSliderElement>
                        <StyledSliderElementInner>
                            <div>
                                <H2>Определяем ваш лингво&shy;портрет</H2>
                                <StyledParagraph>
                                    Все мы&nbsp;говорим на&nbsp;родном языке
                                    по-разному:
                                </StyledParagraph>

                                <StyledParagraph
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    <StyledIcon src={LinesIcon} />
                                    <span>
                                        кто-то быстро и&nbsp;громко выдает все,
                                        что думает;
                                    </span>
                                </StyledParagraph>
                                <StyledParagraph
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    <StyledIcon src={WaveIcon} />
                                    <span>
                                        кто-то медленно, подбирая слова,
                                        формирует длинные речевые конструкции.
                                    </span>
                                </StyledParagraph>

                                <StyledParagraph>
                                    Чтобы не&nbsp;ломать ваш привычный речевой
                                    уклад, на&nbsp;вводном уроке
                                    мы&nbsp;определим ваши речевые привычки,
                                    подберем преподавателя со&nbsp;схожим
                                    лингвопортретом и&nbsp;составим программу
                                    обучения специально под ваш стиль речи.
                                </StyledParagraph>
                            </div>
                            <StyledIllustration src={lingvoPortrait} />
                        </StyledSliderElementInner>
                    </StyledSliderElement>

                    <StyledSliderElement>
                        <StyledSliderElementInner>
                            <div>
                                <H2>Речевые формулы</H2>
                                <StyledParagraph>
                                    Речевые формулы&nbsp;&mdash; это виды
                                    предложений, которыми мы&nbsp;пользуемся,
                                    чтобы выразить все наши мысли, эмоции,
                                    чувства, желания, идеи на&nbsp;родном языке.
                                    В&nbsp;КЭСПА мы&nbsp;учим применять такие
                                    формулы и&nbsp;на&nbsp;английском языке
                                    с&nbsp;той&nbsp;же легкостью, в&nbsp;нужный
                                    момент общения, без долгих раздумий
                                    о&nbsp;грамматическом времени или залоге.
                                </StyledParagraph>

                                <StyledParagraph>
                                    <img
                                        src={formulas}
                                        style={{
                                            width: '400px',
                                            margin: '0 auto',
                                        }}
                                    />
                                </StyledParagraph>
                                <StyledParagraph>
                                    Количество речевых формул, необходимых для
                                    свободного общения именно вам, подбирается
                                    индивидуально, в&nbsp;зависимости
                                    от&nbsp;ваших целей и&nbsp;потребностей.
                                </StyledParagraph>
                            </div>
                            <StyledIllustration src={speechFormulas} />
                        </StyledSliderElementInner>
                    </StyledSliderElement>

                    <StyledSliderElement>
                        <StyledSliderElementInner>
                            <div>
                                <H2>Артикуля&shy;цион&shy;ная память</H2>
                                <StyledParagraph>
                                    Методика КЭСПА задействует артикуляционную
                                    память&nbsp;&mdash; мышечную память лица.
                                    Когда вам нужно сказать какую-то фразу, она
                                    будет не&nbsp;в&nbsp;голове,
                                    а&nbsp;в&nbsp;мышцах губ, языка, голосовых
                                    связках. И&nbsp;вылетит на&nbsp;автомате!
                                </StyledParagraph>
                                <StyledParagraph>
                                    <StyledSpeechDrilling>
                                        <StyledSpeechDrillingIcon
                                            onClick={toggleAudio}
                                        >
                                            {audioPlaying ? (
                                                <VolumeUpIcon />
                                            ) : (
                                                <PlayIcon
                                                    style={{ left: '1px' }}
                                                />
                                            )}
                                        </StyledSpeechDrillingIcon>
                                        Where there's a will, there's a way.
                                    </StyledSpeechDrilling>
                                </StyledParagraph>
                                <StyledParagraph>
                                    {/* DO NOT REMOVE */}
                                </StyledParagraph>
                                <StyledParagraph>
                                    Добиться этого помогут наши специальные
                                    артикуляционно-грамматические упражнения.
                                </StyledParagraph>
                            </div>
                            <StyledIllustration src={articulation} />
                        </StyledSliderElementInner>
                    </StyledSliderElement>
                </Slider>
            </TeachingBlock>
        </React.Fragment>
    );
};
